import React, { useEffect, useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { motion } from "framer-motion";
import {
  Sparkles, Play, Disc3, Music4, Share2, Check, Smartphone, ArrowRight,
  Piano, Drum, SlidersHorizontal, AudioWaveform, Wand2, Zap, Star, Scissors,
} from "lucide-react";

const IMG = {
  hero: "https://images.pexels.com/photos/27141312/pexels-photo-27141312.jpeg",
  producer: "https://images.pexels.com/photos/5749192/pexels-photo-5749192.jpeg",
  dj: "https://images.pexels.com/photos/9005483/pexels-photo-9005483.jpeg",
};

const PROMPTS = [
  "140 BPM dark trap beat with heavy 808s",
  "chill lo-fi hip-hop with a rhodes piano",
  "upbeat house groove at 126 BPM",
  "boom-bap drums with a jazzy bassline",
  "dreamy synthwave with a neon lead",
];

const GENRES = ["Trap", "Lo-Fi", "House", "Boom-Bap", "Synthwave", "Drill", "Afrobeat", "R&B", "Techno", "Pop", "Hip-Hop", "Ambient"];

const fadeUp = {
  initial: { opacity: 0, y: 26 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true, margin: "-70px" },
  transition: { duration: 0.55, ease: "easeOut" },
};

const Label = ({ children, color = "#00e5ff" }) => (
  <span className="font-mono-data text-[11px] uppercase tracking-[0.28em]" style={{ color }}>{children}</span>
);

// Animated "type a vibe → generate" hero mockup
function AIPromptDemo({ onTry }) {
  const [text, setText] = useState("");
  const [phase, setPhase] = useState("typing"); // typing | generating | done
  const idx = useRef(0);
  const char = useRef(0);

  useEffect(() => {
    let t;
    const full = PROMPTS[idx.current];
    if (phase === "typing") {
      if (char.current <= full.length) {
        t = setTimeout(() => {
          setText(full.slice(0, char.current));
          char.current += 1;
        }, 45);
      } else {
        t = setTimeout(() => setPhase("generating"), 550);
      }
    } else if (phase === "generating") {
      t = setTimeout(() => setPhase("done"), 1300);
    } else {
      t = setTimeout(() => {
        idx.current = (idx.current + 1) % PROMPTS.length;
        char.current = 0;
        setText("");
        setPhase("typing");
      }, 2200);
    }
    return () => clearTimeout(t);
  }, [phase, text]);

  return (
    <div
      className="relative rounded-2xl p-5 sm:p-6 border border-[#00e5ff]/25 bg-black/60 backdrop-blur-xl"
      style={{ boxShadow: "0 0 60px rgba(0,229,255,0.12)" }}
      data-testid="ai-demo"
    >
      <div className="flex items-center gap-2 mb-4">
        <span className="w-2.5 h-2.5 rounded-full bg-[#ff007f]" />
        <span className="w-2.5 h-2.5 rounded-full bg-[#ffb800]" />
        <span className="w-2.5 h-2.5 rounded-full bg-[#00ff66]" />
        <Label>AI Song Starter</Label>
      </div>

      <div className="rounded-lg bg-[#050505] border border-[#1f1f1f] px-4 py-3.5 min-h-[54px] flex items-center">
        <Wand2 size={16} className="text-[#00e5ff] mr-2.5 shrink-0" />
        <span className="font-mono-data text-sm sm:text-base text-neutral-200">
          {text}
          <span className="bmxr-caret text-[#00e5ff]">▌</span>
        </span>
      </div>

      <div className="mt-4 h-20 rounded-lg bg-[#050505] border border-[#1f1f1f] flex items-center justify-center gap-[3px] px-4 overflow-hidden">
        {phase === "generating" ? (
          <span className="font-mono-data text-xs text-[#00e5ff] tracking-widest animate-pulse">GENERATING YOUR BEAT…</span>
        ) : phase === "done" ? (
          <div className="flex items-center justify-center gap-[3px] w-full h-full">
            {Array.from({ length: 46 }).map((_, i) => (
              <span
                key={i}
                className="bmxr-bar w-[3px] rounded-full"
                style={{
                  height: "70%",
                  background: i % 2 ? "#ff007f" : "#00e5ff",
                  animationDelay: `${(i % 12) * 0.06}s`,
                }}
              />
            ))}
          </div>
        ) : (
          <span className="font-mono-data text-[11px] text-neutral-600 tracking-widest">describe any sound…</span>
        )}
      </div>

      <div className="mt-3 flex items-center justify-between">
        <span className="font-mono-data text-[11px] text-neutral-500">
          {phase === "done" ? "✓ Drums · chords · melody · key · tempo — ready" : "text → full song idea in seconds"}
        </span>
        <button
          data-testid="ai-demo-try-btn"
          onClick={onTry}
          className="flex items-center gap-1.5 rounded-full bg-[#00e5ff] text-black px-4 py-2 text-xs font-bold hover:-translate-y-0.5 transition-transform"
          style={{ boxShadow: "0 0 20px rgba(0,229,255,0.45)" }}
        >
          <Sparkles size={13} /> Try it free
        </button>
      </div>
    </div>
  );
}

export default function Landing() {
  const navigate = useNavigate();
  const openStudio = () => navigate("/studio");
  const goPro = () => navigate("/studio?upgrade=1");
  const [installPrompt, setInstallPrompt] = useState(null);

  useEffect(() => {
    const onBip = (e) => {
      e.preventDefault();
      setInstallPrompt(e);
    };
    window.addEventListener("beforeinstallprompt", onBip);
    return () => window.removeEventListener("beforeinstallprompt", onBip);
  }, []);

  const installApp = async () => {
    if (installPrompt) {
      installPrompt.prompt();
      const { outcome } = await installPrompt.userChoice;
      if (outcome === "accepted") toast.success("Installing BeatMXR…");
      setInstallPrompt(null);
      return;
    }
    const isIOS = /iphone|ipad|ipod/i.test(navigator.userAgent);
    toast(
      isIOS
        ? 'On iPhone: tap Share, then "Add to Home Screen".'
        : 'Open your browser menu and choose "Install app".',
      { duration: 6000 }
    );
  };

  return (
    <div className="min-h-screen bg-[#0a0a0a] text-white grain overflow-x-hidden">
      {/* NAV */}
      <header className="fixed top-0 inset-x-0 z-50 border-b border-white/5 bg-black/60 backdrop-blur-xl">
        <div className="max-w-7xl mx-auto px-5 sm:px-8 h-16 flex items-center justify-between">
          <div className="flex items-center gap-2.5">
            <img src="/logo.png" alt="BeatMXR" className="w-8 h-8 rounded-md" />
            <span className="font-display font-black tracking-widest text-lg">BEAT<span className="text-[#00e5ff]">MXR</span></span>
          </div>
          <nav className="hidden md:flex items-center gap-8 font-mono-data text-[11px] uppercase tracking-[0.2em] text-neutral-400">
            <a href="#ai" className="hover:text-[#00e5ff] transition-colors">The AI</a>
            <a href="#features" className="hover:text-[#00e5ff] transition-colors">Studio</a>
            <a href="#pricing" className="hover:text-[#00e5ff] transition-colors">Pricing</a>
          </nav>
          <button
            data-testid="nav-cta-btn"
            onClick={openStudio}
            className="rounded-full bg-[#00e5ff] text-black px-5 py-2.5 text-xs font-bold hover:-translate-y-0.5 transition-transform"
            style={{ boxShadow: "0 0 20px rgba(0,229,255,0.4)" }}
          >
            Start Making Beats
          </button>
        </div>
      </header>

      {/* HERO */}
      <section className="relative pt-32 pb-16 sm:pt-40 sm:pb-24 px-5 sm:px-8">
        <div
          className="absolute top-0 right-0 w-[55%] h-[520px] opacity-[0.14] pointer-events-none"
          style={{ background: "radial-gradient(circle at 70% 30%, #00e5ff, transparent 60%)" }}
        />
        <div
          className="absolute top-40 left-0 w-[45%] h-[420px] opacity-[0.12] pointer-events-none"
          style={{ background: "radial-gradient(circle at 20% 40%, #ff007f, transparent 60%)" }}
        />
        <div className="max-w-7xl mx-auto grid lg:grid-cols-2 gap-12 items-center relative">
          <motion.div {...fadeUp}>
            <div className="inline-flex items-center gap-2 rounded-full border border-[#00e5ff]/30 bg-[#00e5ff]/10 px-4 py-1.5 mb-6">
              <Zap size={13} className="text-[#00e5ff]" />
              <Label>AI-Powered Music Studio</Label>
            </div>
            <h1 className="font-display font-black leading-[0.95] tracking-tight text-5xl sm:text-6xl lg:text-7xl">
              Type a vibe.<br />
              <span className="text-[#00e5ff]" style={{ textShadow: "0 0 30px rgba(0,229,255,0.5)" }}>Drop a track.</span>
            </h1>
            <p className="mt-6 text-base sm:text-lg text-neutral-300 max-w-lg leading-relaxed">
              BeatMXR's AI turns a single sentence into a full beat — drums, chords, melody, key and tempo.
              Remove vocals from any song, remix with real turntables and instruments, and share it with a link.
              Create a free account and start in seconds.
            </p>
            <div className="mt-8 flex flex-wrap items-center gap-4">
              <button
                data-testid="open-studio-btn"
                onClick={openStudio}
                className="group flex items-center gap-2 rounded-full bg-[#00e5ff] text-black px-7 py-4 text-sm font-bold hover:-translate-y-1 transition-transform"
                style={{ boxShadow: "0 0 34px rgba(0,229,255,0.5)" }}
              >
                <Sparkles size={16} /> Try the AI Free
                <ArrowRight size={16} className="group-hover:translate-x-1 transition-transform" />
              </button>
              <button
                data-testid="hero-pro-btn"
                onClick={goPro}
                className="flex items-center gap-2 rounded-full border border-white/15 px-7 py-4 text-sm font-bold text-white hover:border-[#ff007f] hover:text-[#ff007f] transition-colors"
              >
                <Star size={15} /> 7-day Pro trial
              </button>
            </div>
            <p className="mt-4 font-mono-data text-[11px] text-neutral-500 tracking-wide">
              No download · Free to start · Runs right in your browser
            </p>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, scale: 0.96, y: 20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            transition={{ duration: 0.7, ease: "easeOut" }}
          >
            <AIPromptDemo onTry={openStudio} />
          </motion.div>
        </div>
      </section>

      {/* GENRE MARQUEE */}
      <div className="border-y border-white/5 py-4 overflow-hidden bg-black/40">
        <div className="bmxr-marquee">
          {[...GENRES, ...GENRES].map((g, i) => (
            <span key={i} className="font-mono-data text-sm tracking-[0.25em] text-neutral-500 mx-6 uppercase">
              {g} <span className="text-[#00e5ff] mx-2">•</span>
            </span>
          ))}
        </div>
      </div>

      {/* AI SHOWCASE */}
      <section id="ai" className="px-5 sm:px-8 py-20 sm:py-28">
        <div className="max-w-7xl mx-auto grid lg:grid-cols-2 gap-14 items-center">
          <motion.div {...fadeUp} className="order-2 lg:order-1">
            <div
              className="relative rounded-2xl overflow-hidden border border-white/10"
              style={{ boxShadow: "0 0 50px rgba(255,0,127,0.15)" }}
            >
              <img src={IMG.hero} alt="AI sound waveform" className="w-full h-[360px] object-cover" />
              <div className="absolute inset-0" style={{ background: "linear-gradient(180deg, transparent, rgba(10,10,10,0.85))" }} />
              <div className="absolute bottom-5 left-5">
                <Label>real output, seconds later</Label>
              </div>
            </div>
          </motion.div>
          <motion.div {...fadeUp} className="order-1 lg:order-2">
            <Label color="#ff007f">The hook</Label>
            <h2 className="font-display font-black text-4xl sm:text-5xl mt-3 leading-[1.02]">
              Your AI <span className="text-[#00e5ff]">co-producer</span>
            </h2>
            <p className="mt-5 text-neutral-300 leading-relaxed">
              Stuck on a blank canvas? Describe the sound in plain English and BeatMXR builds a starting point
              instantly — a full arrangement you can play, tweak and finish. It's the fastest way from idea to track.
            </p>
            <ul className="mt-6 space-y-3">
              {[
                "Text-to-beat: any genre, tempo or mood",
                "Generates drums, chords, melody & bassline",
                "Locks to a musical key so it always sounds right",
                "Instant — no samples to dig, no theory to learn",
              ].map((f) => (
                <li key={f} className="flex items-start gap-2.5 text-sm text-neutral-200">
                  <span className="mt-0.5 w-5 h-5 rounded-full bg-[#00e5ff]/15 flex items-center justify-center shrink-0">
                    <Check size={12} className="text-[#00e5ff]" />
                  </span>
                  {f}
                </li>
              ))}
            </ul>
            <button
              data-testid="ai-section-cta"
              onClick={openStudio}
              className="mt-8 inline-flex items-center gap-2 rounded-full bg-[#00e5ff] text-black px-6 py-3.5 text-sm font-bold hover:-translate-y-1 transition-transform"
              style={{ boxShadow: "0 0 28px rgba(0,229,255,0.45)" }}
            >
              <Wand2 size={16} /> Generate my first beat
            </button>
          </motion.div>
        </div>
      </section>

      {/* BENTO FEATURES */}
      <section id="features" className="px-5 sm:px-8 pb-20 sm:pb-28">
        <div className="max-w-7xl mx-auto">
          <motion.div {...fadeUp} className="mb-10">
            <Label>A full studio in your browser</Label>
            <h2 className="font-display font-black text-4xl sm:text-5xl mt-3">Everything you need to finish the song</h2>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-12 gap-4">
            {/* AI big tile */}
            <motion.div {...fadeUp} className="md:col-span-8 rounded-2xl p-7 border border-[#00e5ff]/25 bg-gradient-to-br from-[#0d1416] to-[#0a0a0a] relative overflow-hidden">
              <div className="absolute -right-10 -top-10 w-52 h-52 rounded-full opacity-20" style={{ background: "radial-gradient(circle, #00e5ff, transparent 60%)" }} />
              <Wand2 size={26} className="text-[#00e5ff]" />
              <h3 className="font-display font-black text-2xl mt-4">AI Song Starter</h3>
              <p className="text-neutral-400 mt-2 max-w-md text-sm leading-relaxed">
                The flagship. One sentence in, a full beat out. Your creative launchpad whenever inspiration is missing.
              </p>
              <div className="mt-5 flex flex-wrap gap-2">
                {["808 trap", "lo-fi", "house", "boom-bap", "synthwave"].map((t) => (
                  <span key={t} className="font-mono-data text-[10px] uppercase tracking-widest px-3 py-1.5 rounded-full border border-white/10 text-neutral-300">{t}</span>
                ))}
              </div>
            </motion.div>

            <motion.div {...fadeUp} className="md:col-span-4 rounded-2xl p-7 border border-white/10 bg-[#121214] relative overflow-hidden">
              <img src={IMG.dj} alt="DJ decks" className="absolute inset-0 w-full h-full object-cover opacity-25" />
              <div className="relative">
                <Disc3 size={24} className="text-[#00e5ff]" />
                <h3 className="font-display font-black text-xl mt-4">DJ Turntables</h3>
                <p className="text-neutral-400 mt-2 text-sm">Two decks, real scratch, crossfader and pitch. Mix your set live.</p>
              </div>
            </motion.div>

            {[
              { icon: Scissors, c: "#00e5ff", t: "Vocal Remover & Sampler", d: "Upload any song — AI splits it into vocals, drums, bass and a full instrumental to flip." },
              { icon: Drum, c: "#ffb800", t: "Playable Drum Machine", d: "Finger-drum on real pads, live-record your beat, tap your own tempo." },
              { icon: Piano, c: "#00ff66", t: "Playable Instruments", d: "Grand piano, synths and a bass fretboard — with scale lock." },
              { icon: SlidersHorizontal, c: "#a855f7", t: "Mixing Console", d: "Per-channel EQ, FX, pan and faders. Pro-grade sound." },
              { icon: AudioWaveform, c: "#22d3ee", t: "Timeline & Arrange", d: "Drag clips onto a multi-track timeline to build full songs." },
            ].map((f) => (
              <motion.div key={f.t} {...fadeUp} className="md:col-span-3 rounded-2xl p-6 border border-white/10 bg-[#121214] hover:border-white/25 transition-colors">
                <f.icon size={22} style={{ color: f.c }} />
                <h3 className="font-display font-black text-lg mt-4">{f.t}</h3>
                <p className="text-neutral-400 mt-2 text-sm">{f.d}</p>
              </motion.div>
            ))}

            <motion.div {...fadeUp} className="md:col-span-12 rounded-2xl p-7 border border-white/10 bg-[#121214] flex flex-col sm:flex-row items-start sm:items-center gap-5 justify-between">
              <div className="flex items-start gap-4">
                <Share2 size={24} className="text-[#ff007f] mt-1" />
                <div>
                  <h3 className="font-display font-black text-xl">Share your song with a link</h3>
                  <p className="text-neutral-400 mt-1.5 text-sm">Send a track to anyone — they can play it right in the browser, no account needed.</p>
                </div>
              </div>
              <button
                data-testid="features-cta"
                onClick={openStudio}
                className="rounded-full border border-white/15 px-6 py-3 text-sm font-bold hover:border-[#00e5ff] hover:text-[#00e5ff] transition-colors shrink-0"
              >
                Open the Studio
              </button>
            </motion.div>
          </div>
        </div>
      </section>

      {/* PRICING */}
      <section id="pricing" className="px-5 sm:px-8 pb-20 sm:pb-28">
        <div className="max-w-5xl mx-auto">
          <motion.div {...fadeUp} className="text-center mb-12">
            <Label>Simple pricing</Label>
            <h2 className="font-display font-black text-4xl sm:text-5xl mt-3">Start free. Go Pro when you're hooked.</h2>
          </motion.div>
          <div className="grid md:grid-cols-2 gap-6">
            <motion.div {...fadeUp} className="rounded-2xl p-8 border border-white/10 bg-[#121214]">
              <Label color="#a1a1aa">Free</Label>
              <div className="mt-3 font-display font-black text-4xl">$0</div>
              <p className="text-neutral-500 text-sm mt-1">Everything to get started</p>
              <ul className="mt-6 space-y-3 text-sm text-neutral-300">
                {["Full studio + AI Song Starter", "5 AI beats per day", "Save up to 3 songs", "Shareable links"].map((f) => (
                  <li key={f} className="flex items-center gap-2.5"><Check size={15} className="text-neutral-500" /> {f}</li>
                ))}
              </ul>
              <button
                data-testid="pricing-free-cta"
                onClick={openStudio}
                className="mt-8 w-full rounded-full border border-white/15 py-3.5 text-sm font-bold hover:border-white/40 transition-colors"
              >
                Start Free
              </button>
            </motion.div>

            <motion.div
              {...fadeUp}
              className="rounded-2xl p-8 border border-[#00e5ff]/50 bg-gradient-to-br from-[#0d1416] to-[#0a0a0a] relative"
              style={{ boxShadow: "0 0 45px rgba(0,229,255,0.18)" }}
            >
              <div className="absolute -top-3 right-6 px-3 py-1 rounded-full bg-[#ff007f] text-white font-mono-data text-[10px] tracking-widest">MOST POPULAR</div>
              <Label>Pro</Label>
              <div className="mt-3 flex items-end gap-2">
                <span className="font-display font-black text-4xl">$9.99</span>
                <span className="text-neutral-500 text-sm mb-1">/mo · or $99.99/yr</span>
              </div>
              <p className="text-[#00e5ff] text-sm mt-1 font-bold">7-day free trial — cancel anytime</p>
              <ul className="mt-6 space-y-3 text-sm text-neutral-200">
                {["AI Beat Lab — 200 Fast + 20 Master beats/mo", "Studio-grade, sellable beats", "Unlimited AI Song Starter & saves", "Export studio-quality WAV", "Everything in Free"].map((f) => (
                  <li key={f} className="flex items-center gap-2.5"><Check size={15} className="text-[#00e5ff]" /> {f}</li>
                ))}
              </ul>
              <button
                data-testid="pricing-pro-cta"
                onClick={goPro}
                className="mt-8 w-full rounded-full bg-[#00e5ff] text-black py-3.5 text-sm font-bold hover:-translate-y-1 transition-transform"
                style={{ boxShadow: "0 0 26px rgba(0,229,255,0.5)" }}
              >
                Start 7-Day Free Trial
              </button>
            </motion.div>
          </div>
        </div>
      </section>

      {/* FINAL CTA */}
      <section className="px-5 sm:px-8 pb-24">
        <motion.div
          {...fadeUp}
          className="max-w-5xl mx-auto rounded-3xl p-10 sm:p-16 text-center border border-[#00e5ff]/25 relative overflow-hidden"
          style={{ background: "linear-gradient(135deg, #0d1416, #0a0a0a)" }}
        >
          <div className="absolute inset-0 opacity-20 pointer-events-none" style={{ background: "radial-gradient(circle at 50% 0%, #00e5ff, transparent 55%)" }} />
          <h2 className="font-display font-black text-4xl sm:text-6xl relative">Your next beat is one sentence away.</h2>
          <p className="text-neutral-300 mt-5 relative max-w-xl mx-auto">Open the studio, type a vibe, and hear it come to life. Free to try — no download.</p>
          <div className="mt-9 flex flex-wrap items-center justify-center gap-4 relative">
            <button
              data-testid="final-cta-btn"
              onClick={openStudio}
              className="flex items-center gap-2 rounded-full bg-[#00e5ff] text-black px-8 py-4 text-sm font-bold hover:-translate-y-1 transition-transform"
              style={{ boxShadow: "0 0 34px rgba(0,229,255,0.55)" }}
            >
              <Sparkles size={16} /> Try the AI Free
            </button>
            <button
              data-testid="pwa-badge"
              onClick={installApp}
              className="flex items-center gap-2 rounded-full border border-white/15 px-6 py-4 text-sm font-bold hover:border-[#00e5ff] hover:text-[#00e5ff] transition-colors"
            >
              <Smartphone size={16} /> Add to Home Screen
            </button>
          </div>
        </motion.div>
      </section>

      {/* FOOTER */}
      <footer className="border-t border-white/5 px-5 sm:px-8 py-10">
        <div className="max-w-7xl mx-auto flex flex-col sm:flex-row items-center justify-between gap-4">
          <div className="flex items-center gap-2.5">
            <img src="/logo.png" alt="BeatMXR" className="w-7 h-7 rounded-md" />
            <span className="font-display font-black tracking-widest">BEAT<span className="text-[#00e5ff]">MXR</span></span>
          </div>
          <p className="font-mono-data text-[11px] text-neutral-600 tracking-wide">Make music anywhere · © {new Date().getFullYear()} BeatMXR</p>
          <div className="flex items-center gap-5">
            <a
              data-testid="footer-support"
              href="/support"
              className="font-mono-data text-[11px] uppercase tracking-[0.2em] text-neutral-400 hover:text-[#00e5ff] transition-colors"
            >
              Support
            </a>
            <button
              data-testid="footer-cta"
              onClick={openStudio}
              className="font-mono-data text-[11px] uppercase tracking-[0.2em] text-[#00e5ff] hover:underline"
            >
              Open Studio →
            </button>
          </div>
        </div>
      </footer>
    </div>
  );
}
